import chroma from "chroma-js";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { patch, postFormData } from "@/lib/api/_client";
import { useTenantTheme } from "@/lib/hooks/use-clients";
import { useT } from "@/lib/i18n/use-t";
import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { T } from "./theme-settings.i18n";

type TenantTheme = {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  chartColors: string[] | null;
  themePrimaryColor: string | null;
};

/**
 * Generate a harmonious color palette from a base color using chroma.js
 * Creates 12 colors with good visual distinction for charts
 */
function generatePaletteFromColor(baseColor: string, count: number = 12): string[] {
  const base = chroma(baseColor);
  const baseHue = base.get("hsl.h");
  const baseSat = base.get("hsl.s");
  const baseLum = base.get("hsl.l");

  const colors: string[] = [];
  colors.push(base.hex());

  for (let i = 1; i < count; i++) {
    const hueOffset = (i * 360) / count;
    const newHue = (baseHue + hueOffset) % 360;
    const satVariation = 0.1 * Math.sin(i * 0.8);
    const lumVariation = 0.08 * Math.cos(i * 1.2);
    const newSat = Math.max(0.3, Math.min(0.9, baseSat + satVariation));
    const newLum = Math.max(0.35, Math.min(0.65, baseLum + lumVariation));
    colors.push(chroma.hsl(newHue, newSat, newLum).hex());
  }

  return colors;
}

// Color picker with inline display
function ColorPickerField({
  label,
  color,
  onChange,
  disabled,
  defaultColor,
  resetLabel,
}: {
  label: string;
  color: string | null;
  onChange: (color: string | null) => void;
  disabled?: boolean;
  defaultColor: string;
  resetLabel: string;
}) {
  const displayColor = color || defaultColor;

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              // A colour swatch has no text and no icon, so without this it
              // announces as "button" and the chosen colour is invisible to a
              // screen reader.
              aria-label={`${label}: ${displayColor}`}
              className="w-12 h-12 rounded-lg border-2 border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all shadow-sm"
              style={{ backgroundColor: displayColor }}
              disabled={disabled}
            />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <HexColorPicker color={displayColor} onChange={onChange} />
            <Input
              type="text"
              value={displayColor}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#000000"
              className="mt-2 w-full font-mono text-sm"
              disabled={disabled}
            />
          </PopoverContent>
        </Popover>
        <span className="font-mono text-sm">{displayColor}</span>
        {color && (
          <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={disabled}>
            {resetLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export function ThemeSettings() {
  const t = useT(T);
  const { theme: serverTheme, isLoading, mutate } = useTenantTheme();
  const [localTheme, setLocalTheme] = useState<TenantTheme | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"light" | "dark" | null>(null);

  // Use local edits if present, otherwise server data
  const theme = localTheme ?? serverTheme;

  // Default brand color
  const defaultBrand = "#ee8438";
  const displayBrand = theme?.themePrimaryColor || defaultBrand;

  const setTheme = (updater: (prev: TenantTheme) => TenantTheme) => {
    setLocalTheme((prev) => {
      const base = prev ??
        serverTheme ?? {
          logoLightUrl: null,
          logoDarkUrl: null,
          chartColors: null,
          themePrimaryColor: null,
        };
      return updater(base);
    });
  };

  const handleLogoUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "light" | "dark",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "image/svg+xml") {
      toast.error(t.invalidFileType, { description: t.invalidFileTypeDescription });
      return;
    }

    if (file.size > 1024 * 1024) {
      toast.error(t.fileTooLarge, { description: t.fileTooLargeDescription });
      return;
    }

    setUploading(type);

    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      uploadData.append("type", "tenant-logo");

      const data = await postFormData<{ url: string }>("/internal/uploads/upload", uploadData);

      if (type === "light") {
        setTheme((prev) => ({ ...prev, logoLightUrl: data.url }));
      } else {
        setTheme((prev) => ({ ...prev, logoDarkUrl: data.url }));
      }
    } catch (_err) {
      toast.error(t.uploadFailed, { description: t.uploadFailedDescription });
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveLogo = (type: "light" | "dark") => {
    if (type === "light") {
      setTheme((prev) => ({ ...prev, logoLightUrl: null }));
    } else {
      setTheme((prev) => ({ ...prev, logoDarkUrl: null }));
    }
  };

  const handleSave = async () => {
    if (!theme) return;
    setSaving(true);
    try {
      await patch("/internal/tenant/theme", theme);
      toast.success(t.themeSettingsSaved);
      setLocalTheme(null);
      mutate();
    } catch (err: any) {
      toast.error(t.error, { description: err.message || t.failedToSaveTheme });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !theme) {
    return null;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.theme }]}
        title={t.theme}
        description={t.description}
      />

      {/* Brand Color */}
      <Card>
        <CardHeader>
          <CardTitle>{t.brandColor}</CardTitle>
          <CardDescription>{t.brandColorDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <ColorPickerField
            label={t.brandColor}
            color={theme.themePrimaryColor}
            onChange={(color) => setTheme((prev) => ({ ...prev, themePrimaryColor: color }))}
            disabled={saving}
            defaultColor={defaultBrand}
            resetLabel={t.reset}
          />
        </CardContent>
      </Card>

      {/* Chart Colors */}
      <Card>
        <CardHeader>
          <CardTitle>{t.chartColors}</CardTitle>
          <CardDescription>{t.chartColorsDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t.colorPalette}</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const palette = generatePaletteFromColor(displayBrand, 12);
                    setTheme((prev) => ({ ...prev, chartColors: palette }));
                  }}
                  disabled={saving}
                >
                  {t.generateFromBrand}
                </Button>
                {theme.chartColors && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTheme((prev) => ({ ...prev, chartColors: null }))}
                    disabled={saving}
                  >
                    {t.resetToDefault}
                  </Button>
                )}
              </div>
            </div>

            {/* Color swatches */}
            <div className="flex flex-wrap gap-2">
              {(theme.chartColors || TABLEAU20_COLORS).map((color, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: color palette where position matters
                <Popover key={index}>
                  <PopoverTrigger asChild>
                    <div className="relative group">
                      <button
                        type="button"
                        aria-label={`${t.color} ${index + 1}: ${color}`}
                        className="w-8 h-8 rounded-md border border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-1"
                        style={{ backgroundColor: color }}
                        disabled={saving}
                      />
                      {theme.chartColors && theme.chartColors.length > 2 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newColors = [...theme.chartColors!];
                            newColors.splice(index, 1);
                            setTheme((prev) => ({ ...prev, chartColors: newColors }));
                          }}
                          aria-label={`${t.remove} ${t.color} ${index + 1}`}
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          disabled={saving}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <HexColorPicker
                      color={color}
                      onChange={(newColor) => {
                        const newColors = theme.chartColors
                          ? [...theme.chartColors]
                          : [...TABLEAU20_COLORS];
                        newColors[index] = newColor;
                        setTheme((prev) => ({ ...prev, chartColors: newColors }));
                      }}
                    />
                    <Input
                      type="text"
                      value={color}
                      onChange={(e) => {
                        const newColors = theme.chartColors
                          ? [...theme.chartColors]
                          : [...TABLEAU20_COLORS];
                        newColors[index] = e.target.value;
                        setTheme((prev) => ({ ...prev, chartColors: newColors }));
                      }}
                      placeholder="#000000"
                      className="mt-2 w-full font-mono text-sm"
                    />
                  </PopoverContent>
                </Popover>
              ))}

              {/* Add color button */}
              <button
                type="button"
                onClick={() => {
                  const currentColors = theme.chartColors || [...TABLEAU20_COLORS];
                  setTheme((prev) => ({
                    ...prev,
                    chartColors: [...currentColors, "#6b7280"],
                  }));
                }}
                aria-label={t.addColor}
                className="w-8 h-8 rounded-md border border-dashed border-input hover:border-foreground/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                disabled={saving}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            <p className="text-xs text-muted-foreground">
              {theme.chartColors
                ? `${theme.chartColors.length} ${t.customColorsCount}`
                : `${t.defaultPaletteCount} (${TABLEAU20_COLORS.length})`}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Logo Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t.logo}</CardTitle>
          <CardDescription>{t.logoDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Light Mode Logo */}
          <div className="space-y-3">
            <Label>{t.logoLightMode}</Label>
            <p className="text-xs text-muted-foreground">{t.logoLightModeDescription}</p>

            {theme.logoLightUrl ? (
              <div className="flex items-center gap-4">
                <div className="border rounded-lg p-4 bg-white">
                  {/* biome-ignore lint/a11y/useAltText: logo preview */}
                  <img src={theme.logoLightUrl} className="h-10 max-w-48" />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveLogo("light")}
                  disabled={saving}
                >
                  {t.remove}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".svg"
                  onChange={(e) => handleLogoUpload(e, "light")}
                  disabled={uploading !== null || saving}
                />
                {uploading === "light" && (
                  <Button disabled size="sm">
                    {t.uploading}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Dark Mode Logo */}
          <div className="space-y-3">
            <Label>{t.logoDarkMode}</Label>
            <p className="text-xs text-muted-foreground">{t.logoDarkModeDescription}</p>

            {theme.logoDarkUrl ? (
              <div className="flex items-center gap-4">
                <div className="border rounded-lg p-4 bg-neutral-900">
                  {/* biome-ignore lint/a11y/useAltText: logo preview */}
                  <img src={theme.logoDarkUrl} className="h-10 max-w-48" />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveLogo("dark")}
                  disabled={saving}
                >
                  {t.remove}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".svg"
                  onChange={(e) => handleLogoUpload(e, "dark")}
                  disabled={uploading !== null || saving}
                />
                {uploading === "dark" && (
                  <Button disabled size="sm">
                    {t.uploading}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} loading={saving}>
          {t.save}
        </Button>
      </div>
    </div>
  );
}
